// @ts-nocheck
import { LocalConnectorElementModel } from '@blocksuite/affine-model';
import type {
  BaseElementProps,
  SerializedElement,
  SurfaceBlockModel,
} from '@blocksuite/block-std/gfx';
import {
  convert,
  field,
  GfxGroupLikeElementModel,
  observe,
  watch,
} from '@blocksuite/block-std/gfx';
import type { SerializedXYWH, XYWH } from '@blocksuite/global/utils';
import {
  assertType,
  deserializeXYWH,
  keys,
  last,
  pick,
} from '@blocksuite/global/utils';
import { DocCollection, type Y } from '@blocksuite/store';
import { generateKeyBetween } from 'fractional-indexing';
import { z } from 'zod';

import { TextResizing } from '../consts.js';
import { ConnectorPathGenerator } from '../managers/connector-manager.js';
import {
  type MindmapNode,
  type MindmapRoot,
  type NodeDetail,
} from './utils/mindmap/layout.js';
import { layout, LayoutType } from './utils/mindmap/layout.js';
import type {
  ConnectorStyle,
  MindmapStyleGetter,
  NodeStyle,
} from './utils/mindmap/style.js';
import {
  applyNodeStyle,
  getDepthScale,
  MindmapStyle,
  mindmapStyleGetters,
} from './utils/mindmap/style.js';

export { LayoutType, type MindmapNode, type NodeDetail } from './utils/mindmap/layout.js';
export { type ConnectorStyle, MindmapStyle } from './utils/mindmap/style.js';

const baseNodeSchema = z.object({
  text: z.string(),
  xywh: z.optional(z.string()),
});

type Node = z.infer<typeof baseNodeSchema> & {
  children?: Node[];
};

const nodeSchema: z.ZodType<Node> = baseNodeSchema.extend({
  children: z.lazy(() => nodeSchema.array()).optional(),
});

type NodeType = z.infer<typeof nodeSchema>;

export function isNodeType(node: Record<string, unknown>): node is NodeType {
  return typeof node.text === 'string' && Array.isArray(node.children);
}

export interface SerializedNode {
  id: string;
  text: string;
  children: SerializedNode[];
}

export type SerializedMindmapElement = SerializedElement & {
  children: Record<string, NodeDetail>;
};

type MindmapElementProps = BaseElementProps & {
  children: Y.Map<NodeDetail>;
};

export class MindmapElementModel extends GfxGroupLikeElementModel<MindmapElementProps> {
  private _nodeMap = new Map<string, MindmapNode>();

  private _queueBuildTree = false;

  private _queued = false;

  private _tree!: MindmapRoot;

  connectors = new Map<string, LocalConnectorElementModel>();

  extraConnectors = new Map<string, LocalConnectorElementModel>();

  pathGenerator: ConnectorPathGenerator = new ConnectorPathGenerator({
    getElementById: (id: string) =>
      this.surface.getElementById(id) ??
      (this.surface.doc.getBlockById(id) as BlockSuite.EdgelessModel),
  });

  static createFromTree(
    tree: MindmapNode,
    style: MindmapStyle,
    layoutType: LayoutType,
    surface: SurfaceBlockModel
  ) {
    const children = new DocCollection.Y.Map();
    const traverse = (subtree: MindmapNode, parent?: string) => {
      const value: NodeDetail = {
        ...subtree.detail,
        parent,
      };

      if (!parent) {
        delete value.parent;
      }

      children.set(subtree.id, value);

      subtree.children.forEach(child => traverse(child, subtree.id));
    };

    traverse(tree);

    const mindmapId = surface.addElement({
      type: 'mindmap',
      children,
      layoutType,
      style,
    });
    const mindmap = surface.getElementById(mindmapId) as MindmapElementModel;

    mindmap.layout();

    return mindmap;
  }

  static override propsToY(props: Record<string, unknown>) {
    if (
      props.children &&
      !isNodeType(props.children as Record<string, unknown>) &&
      !(props.children instanceof DocCollection.Y.Map)
    ) {
      const children: Y.Map<NodeDetail> = new DocCollection.Y.Map();

      keys(props.children).forEach(key => {
        const detail = pick<Record<string, unknown>, keyof NodeDetail>(
          props.children![key],
          ['index', 'parent', 'preferredDir', 'image', 'tags']
        );
        children.set(key as string, detail as NodeDetail);
      });

      props.children = children;
    }

    return props as MindmapElementProps;
  }

  private _cfgBalanceLayoutDir() {
    if (this.layoutType !== LayoutType.BALANCE) {
      return;
    }

    const tree = this._tree;

    // Match the 4-way radial distribution:
    // index % 4: 0->RIGHT, 1->BOTTOM, 2->LEFT, 3->TOP
    // Only put LEFT/RIGHT children in the arrays, not TOP/BOTTOM
    tree.right = [];
    tree.left = [];

    tree.children.forEach((child, idx) => {
      const mod = idx % 4;
      if (mod === 0) {
        tree.right.push(child);
      } else if (mod === 2) {
        tree.left.push(child);
      }
      // mod === 1 (BOTTOM) and mod === 3 (TOP) are not added to left/right
    });

    tree.left.reverse();
  }

  private _isConnectorOutdated(
    options: {
      connector: LocalConnectorElementModel;
      from: MindmapNode;
      to: MindmapNode;
      layout: LayoutType;
    },
    updateKey: boolean = true
  ) {
    const { connector, from, to, layout } = options;
    const cacheKey = `${from.element.xywh}-${to.element.xywh}-${layout}-${this.style}`;

    // @ts-expect-error non-typed cache key on connector instance
    if (connector['MINDMAP_CONNECTOR'] === cacheKey) {
      return { outdated: false, cacheKey };
    } else if (updateKey) {
      // @ts-expect-error non-typed cache key on connector instance
      connector['MINDMAP_CONNECTOR'] = cacheKey;
    }

    return { outdated: true, cacheKey };
  }

  // Determine the first-level ancestor (direct child of root) for a node
  private _getFirstLevelAncestor(node: MindmapNode): MindmapNode | null {
    const root = this._tree;
    if (!root || node === root) return null;

    let current: MindmapNode | null = node;
    while (current && current.detail.parent) {
      const parent = this._nodeMap.get(current.detail.parent) ?? null;
      if (parent === root) {
        return current;
      }
      current = parent;
    }
    return null;
  }

  // Compute the branch side (LEFT | RIGHT | TOP | BOTTOM) that a node belongs to in BALANCE layout
  private _getBranchSide(node: MindmapNode): 'LEFT' | 'RIGHT' | 'TOP' | 'BOTTOM' {
    const root = this._tree;
    const first = this._getFirstLevelAncestor(node);

    if (!root || !first) {
      // default to RIGHT if unknown
      return 'RIGHT';
    }

    if (root.left?.includes(first)) return 'LEFT';
    if (root.right?.includes(first)) return 'RIGHT';

    // Deduce TOP/BOTTOM by comparing the first-level ancestor position to root
    const rootCx = root.element.x + root.element.w / 2;
    const rootCy = root.element.y + root.element.h / 2;
    const ancCx = first.element.x + first.element.w / 2;
    const ancCy = first.element.y + first.element.h / 2;

    // If vertically separated from root, choose vertical; fallback to horizontal by dx
    const dx = ancCx - rootCx;
    const dy = ancCy - rootCy;

    if (Math.abs(dy) >= Math.abs(dx)) {
      return dy < 0 ? 'TOP' : 'BOTTOM';
    }
    return dx < 0 ? 'LEFT' : 'RIGHT';
  }

  // Decide connector endpoints for parent->child
  private _getConnectorEndpoints(
    _from: MindmapNode,
    to: MindmapNode,
    layout: LayoutType
  ): { sourcePos: [number, number]; targetPos: [number, number] } {
    // Respect explicit LEFT/RIGHT layouts
    if (layout === LayoutType.RIGHT) {
      return { sourcePos: [1, 0.5], targetPos: [0, 0.5] };
    }
    if (layout === LayoutType.LEFT) {
      return { sourcePos: [0, 0.5], targetPos: [1, 0.5] };
    }

    // BALANCE (radial): Use branch side mapping, recursively
    const side = this._getBranchSide(to);
    switch (side) {
      case 'LEFT':
        return { sourcePos: [0, 0.5], targetPos: [1, 0.5] };
      case 'RIGHT':
        return { sourcePos: [1, 0.5], targetPos: [0, 0.5] };
      case 'TOP':
        return { sourcePos: [0.5, 0], targetPos: [0.5, 1] };
      case 'BOTTOM':
      default:
        return { sourcePos: [0.5, 1], targetPos: [0.5, 0] };
    }
  }

  protected addConnector(
    from: MindmapNode,
    to: MindmapNode,
    layout: LayoutType,
    connectorStyle: ConnectorStyle,
    extra: boolean = false
  ) {
    const id = `#${from.id}-${to.id}`;

    if (extra) {
      this.extraConnectors.set(id, new LocalConnectorElementModel());
    } else if (this.connectors.has(id)) {
      const connector = this.connectors.get(id)!;
      const { outdated } = this._isConnectorOutdated({
        connector,
        from,
        to,
        layout,
      });

      if (!outdated) {
        return connector;
      }
    } else {
      const connector = new LocalConnectorElementModel();
      // update cache key
      this._isConnectorOutdated({
        connector,
        from,
        to,
        layout,
      });
      this.connectors.set(id, connector);
    }

    const connector = extra
      ? this.extraConnectors.get(id)!
      : this.connectors.get(id)!;

    connector.id = id;

    // Determine connector endpoints for BALANCE recursively via branch-side mapping
    const { sourcePos, targetPos } = this._getConnectorEndpoints(from, to, layout);

    connector.source = {
      id: from.id,
      position: sourcePos,
    };
    connector.target = {
      id: to.id,
      position: targetPos,
    };

    Object.entries(connectorStyle).forEach(([key, value]) => {
      // @ts-expect-error assigning connector style fields dynamically
      connector[key as unknown] = value;
    });

    this.pathGenerator.updatePath(connector);

    return connector;
  }

  addNode(
    /**
     * The parent node id of the new node. If it's null, the node will be the root node
     */
    parent: string | MindmapNode | null,
    sibling?: string | number,
    position: 'before' | 'after' = 'after',
    props: Record<string, unknown> = {},

    /**
     * Force the layout direction of the node.
     * It only works on the first level node with the layout type of BALANCE
     */
    direction?: LayoutType.LEFT | LayoutType.RIGHT
  ) {
    if (parent && typeof parent !== 'string') {
      parent = parent.id;
    }

    assertType<string | null>(parent);

    if (parent && !this._nodeMap.has(parent)) {
      throw new Error(`Parent node ${parent} not found`);
    }

    props['text'] = new DocCollection.Y.Text(
      (props['text'] as string) ?? 'New node'
    );

    const type = (props.type as string) ?? 'shape';
    let id: string;
    this.surface.doc.transact(() => {
      const parentNode = parent ? this._nodeMap.get(parent)! : null;

      if (parentNode) {
        const isBalance =
          this.layoutType === LayoutType.BALANCE &&
          this._tree.id === parentNode.id;

        let index = last(parentNode.children)
          ? generateKeyBetween(last(parentNode.children)!.detail.index, null)
          : 'a0';

        sibling = sibling ?? last(parentNode.children)?.id;
        const siblingNode =
          typeof sibling === 'number'
            ? parentNode.children[sibling]
            : sibling
              ? this._nodeMap.get(sibling)
              : undefined;
        const path = siblingNode
          ? this.getPath(siblingNode)
          : this.getPath(parentNode).concat([0]);
        const style = this.styleGetter.getNodeStyle(
          siblingNode ?? parentNode,
          path
        );

        id = this.surface.addElement({
          type,
          xywh: '[0,0,100,30]',
          textResizing: TextResizing.AUTO_WIDTH,
          maxWidth: false,
          ...props,
          ...style.node,
        });

        if (siblingNode) {
          const siblingIndex = parentNode.children.findIndex(
            val => val.id === sibling
          );

          index =
            position === 'after'
              ? generateKeyBetween(
                siblingNode.detail.index,
                parentNode.children[siblingIndex + 1]?.detail.index ?? null
              )
              : generateKeyBetween(
                parentNode.children[siblingIndex - 1]?.detail.index ?? null,
                siblingNode.detail.index
              );
        } else if (isBalance && direction !== undefined) {
          const lastNode =
            direction === LayoutType.LEFT
              ? this._tree.left[0]
              : last(this._tree.right);

          if (lastNode) {
            index = generateKeyBetween(lastNode.detail.index, null);
          }
        }

        const nodeDetail: NodeDetail = {
          index,
          parent: parent!,
        };

        if (direction !== undefined && isBalance) {
          nodeDetail.preferredDir = direction;
        }

        this.children.set(id, nodeDetail);
      } else {
        const rootStyle = this.styleGetter.root;

        id = this.surface.addElement({
          type,
          xywh: '[0,0,113,41]',
          textResizing: TextResizing.AUTO_WIDTH,
          maxWidth: 400,
          ...props,
          ...rootStyle,
        });

        this.children.clear();
        this.children.set(id, {
          index: 'a0',
        });
      }
    });
    this.layout();

    return id!;
  }

  addTree(
    parent: string | MindmapNode,
    tree: NodeType | MindmapNode,
    /**
     * `sibling` indicates where to insert a subtree among peer elements.
     * If it's a string, it represents a peer element's ID;
     * if it's a number, it represents its index.
     * The subtree will be inserted before the sibling element.
     */
    sibling?: string | number,

    /**
     * Preferred layout direction, only works when parent is root and layout type is BALANCE
     */
    layout?: LayoutType
  ) {
    parent = typeof parent === 'string' ? parent : parent.id;

    if (!this._nodeMap.has(parent) || !parent) {
      return null;
    }

    assertType<string>(parent);

    if (
      layout === LayoutType.BALANCE ||
      this._nodeMap.get(parent) !== this._tree
    ) {
      layout = undefined;
    }

    const traverse = (
      node: NodeType | MindmapNode,
      parent: string,
      sibling?: string | number,
      layout?: LayoutType.LEFT | LayoutType.RIGHT
    ) => {
      let nodeId: string;
      if ('text' in node) {
        nodeId = this.addNode(
          parent,
          sibling,
          'before',
          {
            text: node.text,
          },
          layout
        );
      } else {
        this.children.set(node.id, {
          ...node.detail,
          parent,
        });
        nodeId = node.id;
      }

      node.children?.forEach(child => {
        traverse(child, nodeId);
      });

      return nodeId;
    };

    if (!('text' in tree)) {
      // Modify the children ymap directly hence need transaction
      this.surface.doc.transact(() => {
        traverse(tree, parent, sibling, layout);
      });

      this.applyStyle();
      this.layout();

      return this._nodeMap.get(tree.id);
    } else {
      const nodeId = traverse(tree, parent, sibling, layout);

      this.layout();

      return this._nodeMap.get(nodeId);
    }
  }

  applyStyle(fitContent: boolean = false) {
    this.surface.doc.transact(() => {
      const style = this.styleGetter;
      if (!style) return;
      const rootScale = getDepthScale(0);
      const rootStyled: NodeStyle = {
        ...style.root,
        fontSize: Math.round(style.root.fontSize * rootScale),
        padding: [
          Math.round(style.root.padding[0] * rootScale),
          Math.round(style.root.padding[1] * rootScale),
        ],
      };
      applyNodeStyle(this._tree, rootStyled, fitContent);

      // Apply optional image payload to root element
      {
        const detail = this._tree.detail as NodeDetail;
        const img = detail.image;
        if (img) {
          const el = this._tree.element as unknown as { imageUrl?: string; imageSvg?: string; textDisplay?: boolean; isIcon?: boolean; tags?: NodeDetail['tags'] };
          el.imageUrl = img.url;
          el.imageSvg = img.svg;
          // Only hide text for 'image' nodeType, show both for 'icon'
          el.textDisplay = detail.nodeType !== 'image';
          // Explicitly set isIcon to ensure it persists during edit mode
          el.isIcon = detail.nodeType === 'icon' || false;
        }
        // Apply tags if present
        if (detail.tags) {
          const el = this._tree.element as unknown as { tags?: NodeDetail['tags'] };
          el.tags = detail.tags;
        }
      }

      const walk = (node: MindmapNode, path: number[]) => {
        node.children.forEach((child, idx) => {
          const currentPath = [...path, idx];
          const nodeStyle = style.getNodeStyle(child, currentPath);

          const depth = currentPath.length - 1; // root depth=0, children depth=1
          const factor = getDepthScale(depth);

          if (factor !== 1) {
            const scaledFont = Math.max(10, Math.round(nodeStyle.node.fontSize * factor));
            const scaledPadTop = Math.max(2, Math.round(nodeStyle.node.padding[0] * factor));
            const scaledPadSide = Math.max(4, Math.round(nodeStyle.node.padding[1] * factor));
            const scaledRadius = Math.max(0, Math.round((nodeStyle.node.radius ?? 0) * (factor > 1 ? 1.1 : 0.95)));
            const scaled: NodeStyle = {
              ...nodeStyle.node,
              fontSize: scaledFont,
              padding: [scaledPadTop, scaledPadSide],
              radius: scaledRadius,
            };
            applyNodeStyle(child, scaled, fitContent);
          } else {
            applyNodeStyle(child, nodeStyle.node, fitContent);
          }

          {
            const detail = child.detail as NodeDetail;
            const img = detail.image;
            if (img) {
              const el = child.element as unknown as { imageUrl?: string; imageSvg?: string; textDisplay?: boolean; isIcon?: boolean; tags?: NodeDetail['tags'] };
              el.imageUrl = img.url;
              el.imageSvg = img.svg;
              // Only hide text for 'image' nodeType, show both for 'icon'
              el.textDisplay = detail.nodeType !== 'image';
              // Explicitly set isIcon to ensure it persists during edit mode
              el.isIcon = detail.nodeType === 'icon' || false;
            }
            // Apply tags if present
            if (detail.tags) {
              const el = child.element as unknown as { tags?: NodeDetail['tags'] };
              el.tags = detail.tags;
            }
          }

          walk(child, currentPath);
        });
      };

      walk(this._tree, [0]);
    });
  }

  protected buildTree() {
    const mindmapNodeMap = new Map<string, MindmapNode>();
    const nodesMap = this.children;
    let hasMissingElement = false;

    // The element may be removed
    if (!nodesMap) {
      this._nodeMap = mindmapNodeMap;
      // @ts-expect-error nullable during teardown
      this._tree = null;
      return;
    }

    let rootNode: MindmapRoot | undefined;
    const getElementById = (id: string) => {
      const element = this.surface.getElementById(id);
      if (!element) {
        hasMissingElement = true;
      }
      return element;
    };

    nodesMap.forEach((val, id) => {
      const node = mindmapNodeMap.has(id)
        ? mindmapNodeMap.get(id)!
        : ({
          id,
          parent: val.parent,
          index: val.index,
          detail: val,
          element: getElementById(id),
          children: [],
        } as MindmapNode);

      if (!node.detail) {
        node.detail = val;
      }

      if (!mindmapNodeMap.has(id)) {
        mindmapNodeMap.set(id, node);
      }

      if (!val.parent) {
        rootNode = node as MindmapRoot;
        rootNode.left = [];
        rootNode.right = [];
      } else if (mindmapNodeMap.has(val.parent)) {
        const parentNode = mindmapNodeMap.get(val.parent)!;
        parentNode.children = parentNode.children ?? [];
        parentNode.children.push(node);
      } else {
        mindmapNodeMap.set(val.parent, {
          id: val.parent,
          children: [node],
          element: getElementById(val.parent),
        } as MindmapNode);
      }
    });

    if (hasMissingElement) {
      this.requestBuildTree();
      return;
    }

    mindmapNodeMap.forEach(node => {
      node.children.sort((a, b) =>
        a.detail.index === b.detail.index
          ? 0
          : a.detail.index > b.detail.index
            ? 1
            : -1
      );
    });

    if (!rootNode) {
      return;
    }

    this._nodeMap = mindmapNodeMap;
    this._tree = rootNode;

    if (this.layoutType === LayoutType.BALANCE) {
      this._cfgBalanceLayoutDir();
    } else {
      this._tree[this.layoutType === LayoutType.RIGHT ? 'right' : 'left'] =
        this._tree.children;
    }

    this._restoreCollapsedState();
  }

  private _restoreCollapsedState() {
    const restoreVisibility = (node: MindmapNode, parentCollapsed: boolean) => {
      if (parentCollapsed) {
        node.element.display = false;
      }

      const isCollapsed = node.detail.collapsed ?? false;
      node.children.forEach(child => {
        restoreVisibility(child, parentCollapsed || isCollapsed);
      });
    };

    if (this._tree) {
      this._tree.children.forEach(child => {
        restoreVisibility(child, this._tree.detail.collapsed ?? false);
      });
    }
  }

  /**
   * Detach a mindmap. It is similar to `removeChild` but
   * it does not delete the node.
   *
   * So the node can be used to create a new mind map or merge into other mind map
   */
  detach(subtree: string | MindmapNode) {
    subtree =
      typeof subtree === 'string' ? this._nodeMap.get(subtree)! : subtree;

    assertType<MindmapNode>(subtree);

    if (!subtree) {
      return;
    }

    const traverse = (subtree: MindmapNode) => {
      this.children.delete(subtree.id);

      // cut the reference inside the ymap
      subtree.detail = {
        ...subtree.detail,
      };

      subtree.children.forEach(child => traverse(child));
    };

    this.surface.doc.transact(() => {
      traverse(subtree);
    });

    this.layout();

    delete subtree.detail.parent;

    return subtree;
  }

  /**
   *
   * @param subtree The subtree of root, this only take effects when the layout type is BALANCED.
   * @returns
   */
  getChildNodes(id: string, subtree?: 'left' | 'right') {
    const node = this._nodeMap.get(id);

    if (!node) {
      return [];
    }

    if (subtree && id === this._tree.id) {
      return this._tree[subtree];
    }

    return node.children;
  }

  getConnector(from: MindmapNode, to: MindmapNode) {
    if (!this._nodeMap.has(from.id) || !this._nodeMap.has(to.id)) {
      return null;
    }

    return this.addConnector(
      from,
      to,
      this.getLayoutDir(to)!,
      this.styleGetter.getNodeStyle(to, this.getPath(to)).connector
    );
  }

  getLayoutDir(node: string | MindmapNode): LayoutType | null {
    node = typeof node === 'string' ? this._nodeMap.get(node)! : node;

    assertType<MindmapNode>(node);

    let current: MindmapNode | null = node;
    const root = this._tree;

    while (current) {
      if (current.overriddenDir !== undefined) {
        return current.overriddenDir;
      }

      const parent: MindmapNode | null = current.detail.parent
        ? (this._nodeMap.get(current.detail.parent) ?? null)
        : null;

      if (parent === root) {
        return root.left.includes(current)
          ? LayoutType.LEFT
          : root.right.includes(current)
            ? LayoutType.RIGHT
            : this.layoutType;
      }

      current = parent;
    }

    return this.layoutType;
  }

  getNode(id: string) {
    return this._nodeMap.get(id) ?? null;
  }

  getParentNode(id: string) {
    const node = this.children.get(id);

    return node?.parent ? (this._nodeMap.get(node.parent) ?? null) : null;
  }

  /**
   * Path is an array of indexes that represent the path from the root node to the target node.
   * The first element of the array is always 0, which represents the root node.
   * @param element
   * @returns
   *
   * @example
   * ```ts
   * const path = mindmap.getPath('nodeId');
   * // [0, 1, 2]
   * ```
   */
  getPath(element: string | MindmapNode) {
    let node = this._nodeMap.get(
      typeof element === 'string' ? element : element.id
    );

    if (!node) {
      throw new Error('Node not found');
    }

    const path: number[] = [];

    while (node && node !== this._tree) {
      const parent = this._nodeMap.get(node!.detail.parent!);

      path.unshift(parent!.children.indexOf(node!));
      node = parent;
    }

    path.unshift(0);

    return path;
  }

  /**
   * Get a node by its path. This traverses the tree structure directly
   * and is NOT affected by collapsed state.
   * @param path - Array of indexes from root to target node, e.g. [0, 1, 2]
   * @returns The node at the specified path, or null if not found
   *
   * @example
   * ```ts
   * const node = mindmap.getNodeByPath([0, 1, 2]);
   * // Returns the node at path [0, 1, 2] regardless of collapsed state
   * ```
   */
  getNodeByPath(path: number[]): MindmapNode | null {
    if (!this._tree || !Array.isArray(path) || path.length === 0) {
      return null;
    }

    if (path[0] !== 0) {
      return null;
    }

    if (path.length === 1) {
      return this._tree;
    }

    let current: MindmapNode = this._tree;

    for (let i = 1; i < path.length; i++) {
      const childIndex = path[i];
      const child = current.children[childIndex];

      if (!child) {
        return null;
      }

      current = child;
    }

    return current;
  }

  getSiblingNode(
    id: string,
    direction: 'prev' | 'next' = 'next',
    /**
     * The subtree of which that the sibling node belongs to,
     * this is used when the layout type is BALANCED.
     */
    subtree?: 'left' | 'right'
  ) {
    const node = this._nodeMap.get(id);

    if (!node) {
      return null;
    }

    const parent = this._nodeMap.get(node.detail.parent!);

    if (!parent) {
      return null;
    }

    const childrenTree =
      subtree && parent.id === this._tree.id
        ? this._tree[subtree]
        : parent.children;
    const idx = childrenTree.indexOf(node);
    if (idx === -1) {
      return null;
    }
    const siblingIndex = direction === 'next' ? idx + 1 : idx - 1;
    const sibling = childrenTree[siblingIndex] ?? null;

    return sibling;
  }

  layout(
    tree: MindmapNode | MindmapRoot = this.tree,
    applyStyle = true,
    layoutType?: LayoutType
  ) {
    if (!tree || !tree.element) return;

    if (applyStyle) {
      this.applyStyle(true);
    }

    this.surface.doc.transact(() => {
      const path = this.getPath(tree.id);
      layout(tree, this, layoutType ?? this.getLayoutDir(tree.id), path);
    });
  }

  moveTo(targetXYWH: SerializedXYWH | XYWH) {
    const { x, y } = this;
    const targetPos =
      typeof targetXYWH === 'string' ? deserializeXYWH(targetXYWH) : targetXYWH;
    const offsetX = targetPos[0] - x;
    const offsetY = targetPos[1] - y + targetPos[3];

    this.surface.doc.transact(() => {
      this.childElements.forEach(el => {
        const deserializedXYWH = deserializeXYWH(el.xywh);

        el.xywh =
          `[${deserializedXYWH[0] + offsetX},${deserializedXYWH[1] + offsetY},${deserializedXYWH[2]},${deserializedXYWH[3]}]` as SerializedXYWH;
      });
    });
  }

  moveTree(
    tree: MindmapNode,
    parent: string | MindmapNode,
    siblingIndex: number,
    layout?: LayoutType
  ) {
    parent = this._nodeMap.get(
      typeof parent === 'string' ? parent : parent.id
    )!;

    if (!parent || !this._nodeMap.has(tree.id)) {
      return;
    }

    assertType<MindmapNode>(parent);

    if (layout === LayoutType.BALANCE || parent !== this._tree) {
      layout = undefined;
    }

    const sibling = parent.children[siblingIndex];
    const preSibling = parent.children[siblingIndex - 1];
    const index =
      sibling || preSibling
        ? generateKeyBetween(
          preSibling?.detail.index ?? null,
          sibling?.detail.index ?? null
        )
        : (tree.detail.index ?? undefined);

    this.surface.doc.transact(() => {
      const val: NodeDetail =
        layout !== undefined
          ? {
            ...tree.detail,
            index,
            parent: parent.id,
            preferredDir: layout,
          }
          : {
            ...tree.detail,
            index,
            parent: parent.id,
          };

      this.children.set(tree.id, val);
    });

    this.layout();

    return this._nodeMap.get(tree.id);
  }

  override onCreated(): void {
    this.requestBuildTree();
  }

  removeChild(id: string) {
    if (!this._nodeMap.has(id)) {
      return;
    }

    const surface = this.surface;
    const removedDescendants: string[] = [];
    const remove = (element: MindmapNode) => {
      element.children?.forEach(child => {
        remove(child);
      });

      this.children?.delete(element.id);
      removedDescendants.push(element.id);
    };

    surface.doc.transact(() => {
      remove(this._nodeMap.get(id)!);
    });

    queueMicrotask(() => {
      removedDescendants.forEach(id => surface.removeElement(id));
    });

    // This transaction may not end
    // force to build the elements
    this.buildTree();
    this.requestLayout();
  }

  protected requestBuildTree() {
    if (this._queueBuildTree) {
      return;
    }

    this._queueBuildTree = true;
    queueMicrotask(() => {
      this.buildTree();
      this._queueBuildTree = false;
    });
  }

  requestLayout() {
    if (!this._queued) {
      this._queued = true;

      queueMicrotask(() => {
        this.layout();
        this._queued = false;
      });
    }
  }

  override serialize() {
    const result = super.serialize();
    return result as SerializedMindmapElement;
  }

  stashTree(node: MindmapNode | string) {
    const mindNode = typeof node === 'string' ? this.getNode(node) : node;

    if (!mindNode) {
      return;
    }

    const stashed = new Set<
      BlockSuite.SurfaceElementModel | LocalConnectorElementModel
    >();
    const traverse = (node: MindmapNode, parent: MindmapNode | null) => {
      node.element.stash('xywh');
      node.element.opacity = 0.1;
      stashed.add(node.element);

      if (parent) {
        const connectorId = `#${parent.element.id}-${node.element.id}`;
        const connector = this.connectors.get(connectorId);

        if (connector) {
          connector.opacity = 0.1;
          stashed.add(connector);
        }
      }

      if (node.children.length) {
        node.children.forEach(child => traverse(child, node));
      }
    };

    const parent = this.getParentNode(mindNode.element.id);
    const parentNode = parent ? this.getNode(parent.id) : null;

    traverse(mindNode, parentNode);

    return () => {
      stashed.forEach(el => {
        if ('pop' in el) {
          el.pop('xywh');
        }

        el.opacity = 1;
      });
    };
  }

  traverse(
    callback: (node: MindmapNode, parent: MindmapNode | null) => void,
    options: { stopOnCollapse?: boolean } = {}
  ) {
    const { stopOnCollapse = false } = options;
    const traverse = (node: MindmapNode, parent: MindmapNode | null) => {
      callback(node, parent);

      if (stopOnCollapse && node.detail.collapsed) {
        return;
      }

      node?.children.forEach(child => {
        traverse(child, node);
      });
    };

    if (this._tree) {
      traverse(this._tree, null);
    }
  }

  toggleCollapse(node: MindmapNode, options: { layout?: boolean } = {}) {
    if (!this._nodeMap.has(node.id)) {
      return;
    }

    const { layout: shouldLayout = false } = options;

    if (node && node.children.length > 0) {
      const collapsed = node.detail.collapsed ? false : true;
      const isExpand = !collapsed;

      const changeNodesVisibility = (childNode: MindmapNode) => {
        childNode.element.display = !collapsed;

        if (isExpand && childNode.detail.collapsed) {
          return;
        }

        childNode.children.forEach(child => {
          changeNodesVisibility(child);
        });
      };

      node.children.forEach(changeNodesVisibility);

      this.surface.doc.transact(() => {
        this.children.set(node.id, {
          ...node.detail,
          collapsed,
        });
      });
    }

    if (shouldLayout) {
      this.requestLayout();
    }
  }

  getCollapseCount(node: MindmapNode): number {
    if (!node.detail.collapsed) {
      return 0;
    }
    let count = 0;
    const countChildren = (n: MindmapNode) => {
      count += n.children.length;
      n.children.forEach(countChildren);
    };
    countChildren(node);
    return count;
  }

  get nodeMap() {
    return this._nodeMap;
  }

  override get rotate() {
    return 0;
  }

  override set rotate(_: number) { }

  get styleGetter(): MindmapStyleGetter {
    return mindmapStyleGetters[this.style];
  }

  get tree() {
    return this._tree;
  }

  get type() {
    return 'mindmap';
  }

  @convert((initialValue, instance) => {
    if (!(initialValue instanceof DocCollection.Y.Map)) {
      nodeSchema.parse(initialValue);

      assertType<NodeType>(initialValue);

      const map: Y.Map<NodeDetail> = new DocCollection.Y.Map();
      const surface = instance.surface;
      const doc = surface.doc;
      const recursive = (
        node: NodeType,
        parent: string | null = null,
        index: string = 'a0'
      ) => {
        const nodeWithDetail = node as { image?: NodeDetail['image']; nodeType?: NodeDetail['nodeType']; tags?: NodeDetail['tags'] };
        
        const id = surface.addElement({
          type: 'shape',
          text: node.text,
          xywh: node.xywh ? node.xywh : `[0, 0, 100, 30]`,
          // Pass tags directly to the shape element
          tags: nodeWithDetail.tags,
        } as any);
        const detail: NodeDetail = {
          index,
          parent: parent ?? undefined,
        };
        if (nodeWithDetail.image) {
          detail.image = nodeWithDetail.image;
        }
        if (nodeWithDetail.nodeType) {
          detail.nodeType = nodeWithDetail.nodeType;
        }
        if (nodeWithDetail.tags) {
          detail.tags = nodeWithDetail.tags;
        }
        map.set(id, detail);

        let curIdx = 'a0';
        node.children?.forEach(childNode => {
          recursive(childNode, id, curIdx);
          curIdx = generateKeyBetween(curIdx, null);
        });
      };

      doc.transact(() => {
        recursive(initialValue);
      });

      instance.requestBuildTree();
      instance.requestLayout();
      return map;
    } else {
      instance.requestBuildTree();
      instance.requestLayout();
      return initialValue;
    }
  })
  @observe(
    (_, instance: MindmapElementModel, transaction: Y.Transaction | null) => {
      instance.setChildIds(
        Array.from(instance.children.keys()),
        transaction?.local ?? true
      );

      instance.buildTree();
      instance.connectors.clear();
    }
  )
  @field()
  accessor children: Y.Map<NodeDetail> = new DocCollection.Y.Map();

  @watch((_, instance: MindmapElementModel, local) => {
    if (!local) {
      return;
    }

    instance.surface.doc.transact(() => {
      instance['_tree']?.children.forEach(child => {
        if (!instance.children.has(child.id)) {
          return;
        }

        instance.children.set(child.id, {
          index: child.detail.index,
          parent: child.detail.parent,
        });
      });
    });

    instance.buildTree();
  })
  @field()
  accessor layoutType: LayoutType = LayoutType.RIGHT;

  @watch((_, instance: MindmapElementModel, local) => {
    if (local) {
      instance.layout();
    }
  })
  @field()
  accessor style: MindmapStyle = MindmapStyle.ONE;

  serializeWithText(): { mindmapStructure: SerializedNode | null } {
    const extractNodeStructure = (node: MindmapNode): SerializedNode => {
      const elWithText = node.element as { text?: { toString(): string } };
      const text = elWithText.text ? elWithText.text.toString() : '';
      const id = node.element.id;

      return {
        id,
        text,
        children: node.children ? node.children.map((child: MindmapNode) => extractNodeStructure(child)) : []
      };
    };

    const mindmapStructure = this.tree ? extractNodeStructure(this.tree) : null;

    return {
      mindmapStructure
    };
  }

}

declare global {
  namespace BlockSuite {
    interface SurfaceGroupLikeModelMap {
      // @ts-expect-error vendored blocksuite
      mindmap: MindmapElementModel;
    }
  }
}
